/** A single dated entry in the What's New changelog. */
export interface WhatsNewEntry {
    /** ISO date string (YYYY-MM-DD) -- the merge date of the change on main. */
    date: string;
    /** Short user-facing title. */
    title: string;
    /** One or two sentences describing what a user can now do or what was fixed. */
    description: string;
    /**
     * Source PR numbers from the pdf-bim repository.
     * The repo is private; these are kept for traceability but not linked from the page.
     */
    prs: readonly number[];
}

/**
 * Curated user-facing changelog entries from merged pdf-bim pull requests
 * that touch apps/pdfmarkuptool. Newest first. Internal-only changes (CI,
 * refactors, analytics plumbing) are excluded.
 */
export const WHATS_NEW_ENTRIES: readonly WhatsNewEntry[] = [
    // -- October 2026 --
    {
        date: '2026-10-15',
        title: 'Attach photos to any markup',
        description:
            'Drag and drop a photo onto the drawing, paste from clipboard, or capture from your device camera. Attach the image to a markup with a room label and report-number tag.',
        prs: [526, 527],
    },
    {
        date: '2026-10-15',
        title: 'Photo tray: capture now, place on the drawing later',
        description:
            'Photos you capture or paste go into a tray so you can place them on the drawing whenever you are ready. Nothing is lost if you take a photo mid-session.',
        prs: [532],
    },
    {
        date: '2026-10-15',
        title: 'Photos shared with all collaborators in real time',
        description:
            'In shared sessions, photos attached to markups are automatically available to every collaborator. Photos are stored by content hash, so the same image costs nothing to re-attach.',
        prs: [531],
    },
    {
        date: '2026-10-14',
        title: 'Photos survive PDF export and re-import',
        description:
            'Photo pins and images export into the annotated PDF and are restored when you re-import that PDF. Photo report pages are included in the export.',
        prs: [523],
    },
    {
        date: '2026-10-14',
        title: 'Markup changes survive a reload in shared sessions',
        description:
            'Changes you make in a shared session are cached locally. If you reload or lose connectivity, your work is restored automatically when the session reconnects.',
        prs: [519],
    },
    {
        date: '2026-10-13',
        title: 'Measure distances on PDF plans with scale calibration',
        description:
            'A new dimension tool draws architectural dimension lines with witness marks. Calibrate once to a known distance on the plan and every dimension you draw reads the real-world measurement.',
        prs: [505],
    },
    {
        date: '2026-10-13',
        title: 'Apple Pencil input improvements',
        description:
            'Several Apple Pencil fixes: the first tap after typing is no longer swallowed, callout boxes grow with their text, and panning no longer drops photo placements.',
        prs: [498, 533, 537],
    },
    {
        date: '2026-10-13',
        title: 'Double-click text to select a word',
        description:
            'Double-clicking a word inside a text or callout markup now selects just that word, matching the standard text-editing behavior you expect.',
        prs: [496],
    },
    {
        date: '2026-10-12',
        title: 'iPad page-switch freeze fixed',
        description:
            'A canvas pool fix eliminates a freeze that occurred when switching between pages on iPad. All dropped canvases are now released correctly.',
        prs: [494],
    },
    {
        date: '2026-10-09',
        title: 'Scroll to pan; Ctrl+scroll to zoom',
        description:
            'Scrolling the drawing now pans it directly. Hold Ctrl (or Cmd on Mac) while scrolling to zoom without switching tools.',
        prs: [485],
    },
    {
        date: '2026-10-09',
        title: 'New viewer layout with side drawers',
        description:
            'The viewer now uses a plan-chest layout with side drawers for Sheets, Markups, Data, and Tasks. The pen tray sits on the right edge so tools are always reachable.',
        prs: [455, 460, 464, 486],
    },
    {
        date: '2026-10-08',
        title: 'Tap a section head to jump to the referenced view',
        description:
            'PDF drawings that include section-head markers now support in-document navigation. Tap a section head and the viewer jumps to the referenced view automatically.',
        prs: [482, 483],
    },
    {
        date: '2026-10-07',
        title: 'Redesigned review and approval workflow',
        description:
            'The review panel is rebuilt with a cleaner rail, status badges on each item, and a prominent your-turn indicator that surfaces what needs attention.',
        prs: [480],
    },
    // -- September 2026 --
    {
        date: '2026-09-29',
        title: 'Auto input: Apple Pencil draws, fingers navigate',
        description:
            'Once an Apple Pencil is detected, fingers automatically pan and zoom the drawing instead of drawing strokes. Switch back at any time, or use a mouse for standard pointer behavior.',
        prs: [423],
    },
    {
        date: '2026-09-29',
        title: 'Dimension line endpoints snap to PDF lines',
        description:
            'When drawing dimension lines, endpoints snap onto edges and lines in the PDF drawing. Works with Apple Pencil, touch, and mouse.',
        prs: [424],
    },
    {
        date: '2026-09-24',
        title: 'Draw tools stay active between strokes',
        description:
            'The selected draw tool now stays active between strokes on touch and Pencil input. You no longer have to re-tap the tool after each mark.',
        prs: [376],
    },
    {
        date: '2026-09-24',
        title: 'Hold to snap a stroke to the nearest shape',
        description:
            'Hold the Apple Pencil steady at the end of a stroke to snap it to the nearest markup shape. Snapped callout anchors open for editing immediately.',
        prs: [375, 380],
    },
    // -- August 2026 --
    {
        date: '2026-08-24',
        title: 'Smooth scroll zoom with focal-point tracking',
        description:
            'Zooming with the scroll wheel now stays smooth and keeps the point under the cursor fixed. Pan clamps correctly so the drawing does not disappear when zoomed out.',
        prs: [319, 320],
    },
    {
        date: '2026-08-13',
        title: 'Arrow keys nudge selected markups',
        description:
            'Select one or more markups and press an arrow key to nudge them in any direction. Each keypress moves the selection by one point.',
        prs: [272],
    },
    {
        date: '2026-08-13',
        title: 'Markup styles remembered per tool',
        description:
            'The color, line weight, and fill you set for a markup are remembered per markup type. Pick the same tool again and it opens with your last-used style.',
        prs: [273],
    },
    {
        date: '2026-08-13',
        title: 'Import annotations.json to restore markup data',
        description:
            'Markup data exported as annotations.json can now be re-imported into the same drawing, closing the round-trip for teams sharing markup data as separate files.',
        prs: [290],
    },
    {
        date: '2026-08-05',
        title: 'Markup layers with a tree view',
        description:
            'The markups list is replaced by a layered tree. Create layers, rename them, set one as active, and show or hide each layer independently. Layers export as real PDF optional content groups.',
        prs: [199, 201, 246],
    },
    // -- July 2026 --
    {
        date: '2026-07-27',
        title: 'Show or hide PDF layers',
        description:
            'Layer visibility controls let you show and hide individual PDF layers in the drawing. Layer state is preserved across sessions.',
        prs: [193],
    },
];
